import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAppNavigate } from "../lib/route-primitives";
import { useTranslation } from "react-i18next";
import type { ActivityPayload } from "@wedding/shared";
import { Camera, Heart, MessageCircle, X } from "lucide-react";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { queryClient } from "../lib/query-client";
import { queryKeys } from "../lib/query-keys";

interface ActivityToastProps {
  toast: ActivityPayload & { key: number };
  slug: string;
  onDismiss: () => void;
}

function getEventIcon(eventType: ActivityPayload["eventType"]) {
  switch (eventType) {
    case "new_upload": return Camera;
    case "new_like": return Heart;
    case "new_comment": return MessageCircle;
  }
}

function getEventColor(eventType: ActivityPayload["eventType"]) {
  switch (eventType) {
    case "new_upload": return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
    case "new_like": return "bg-red-500/10 text-red-500";
    case "new_comment": return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
  }
}

export function ActivityToast({ toast, slug, onDismiss }: ActivityToastProps) {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pageActive, setPageActive] = useState(
    () => !document.hidden && document.hasFocus()
  );

  useEffect(() => {
    const show = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(show);
  }, []);

  // Track page visibility + window focus — pause timer when tab is hidden or unfocused
  useEffect(() => {
    const update = () => setPageActive(!document.hidden && document.hasFocus());
    document.addEventListener("visibilitychange", update);
    window.addEventListener("focus", update);
    window.addEventListener("blur", update);
    return () => {
      document.removeEventListener("visibilitychange", update);
      window.removeEventListener("focus", update);
      window.removeEventListener("blur", update);
    };
  }, []);

  // Auto-dismiss after 5s — paused when hovered OR page not active
  const paused = hovered || !pageActive;
  useEffect(() => {
    if (paused) return;
    const hide = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 300);
    }, 5000);
    return () => clearTimeout(hide);
  }, [paused, onDismiss]);

  const handleClick = useCallback(() => {
    // Bir-adım dolaylama (`navigate(galleryPath)`) artık kör nokta DEĞİL:
    // `galleryPath` `RoutePath` markası taşır ve `useAppNavigate` yalnız onu
    // kabul eder. Elle yazılmış bir literal buraya kaç ara değişkenden geçerse
    // geçsin ulaşamaz — tsc reddeder (2026-07-28, İş 1/2).
    const galleryPath = paths.wedding.gallery(slug);
    const isOnGallery = location.pathname === galleryPath;

    if (isOnGallery) {
      // Already on gallery — invalidate queries to refresh + open lightbox
      queryClient.invalidateQueries({ queryKey: queryKeys.media.all(slug) });
    }

    navigate(galleryPath, {
      state: { openMediaId: toast.mediaId },
      replace: isOnGallery,
    });
    onDismiss();
  }, [navigate, location.pathname, slug, toast.mediaId, onDismiss]);

  const Icon = getEventIcon(toast.eventType);
  const iconColor = getEventColor(toast.eventType);
  const name = toast.actorName || t("activity.unknownGuest");
  const ctx = toast.mediaKind === "video" ? "video" : undefined;

  const message = (() => {
    switch (toast.eventType) {
      case "new_upload":
        return t("activity.toast.newUpload", { name, context: ctx });
      case "new_like":
        return t("activity.toast.newLike", { name, context: ctx });
      case "new_comment":
        return t("activity.toast.newComment", { name });
    }
  })();

  // Thumbnail from R2
  const thumbUrl = toast.mediaR2Key ? `/cdn/${toast.mediaR2Key}` : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === "Enter") handleClick(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        "group flex items-center gap-3 rounded-2xl border border-primary/10 bg-background/95 px-4 py-3 shadow-xl backdrop-blur-md transition-all duration-300 cursor-pointer w-full max-w-sm",
        "hover:shadow-2xl hover:border-primary/20 hover:scale-[1.01]",
        visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
      )}
    >
      {/* Thumbnail or Icon */}
      {thumbUrl ? (
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg">
          <img
            src={thumbUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : (
        <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", iconColor)}>
          <Icon className="h-5 w-5" />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{message}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("activity.toast.see")}
        </p>
      </div>

      {/* Dismiss */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setVisible(false);
          setTimeout(onDismiss, 300);
        }}
        className="shrink-0 rounded-full p-1 text-muted-foreground/50 transition-colors hover:text-foreground hover:bg-muted cursor-pointer opacity-0 group-hover:opacity-100"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

interface ToastEntry extends ActivityPayload {
  key: number;
}

interface ActivityToastContainerProps {
  toasts: ToastEntry[];
  slug: string;
  onDismiss: (key: number) => void;
}

export function ActivityToastContainer({
  toasts,
  slug,
  onDismiss,
}: ActivityToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-20 left-0 right-0 z-50 flex flex-col items-center gap-2 px-4 md:bottom-6">
      {toasts.map((t) => (
        <ActivityToast
          key={t.key}
          toast={t}
          slug={slug}
          onDismiss={() => onDismiss(t.key)}
        />
      ))}
    </div>
  );
}
